// Запись своего голоса (повтор за диктором), .scratch/self-recording/spec.md.
// Чистые помощники для components/record-self.tsx и страницы /speak:
// выбор формата MediaRecorder, подпись таймера и наборы трудных звуков.
// Запись живёт только в памяти страницы: ни сервера, ни хранилища.

// Дольше не нужно: слово или короткая фраза. Ограничение спасает от
// забытой кнопки «Стоп» и растущего буфера в памяти телефона.
export const MAX_RECORD_MS = 12_000

// Форматы по убыванию предпочтения. Chrome и Android пишут webm/opus, Safari
// на iPhone и Mac — только mp4. Проигрываем запись на том же устройстве, где
// записали, поэтому любой поддержанный формат годится.
const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/webm', 'audio/ogg;codecs=opus']

// null — ни один кандидат не поддержан: MediaRecorder создаём без mimeType,
// браузер возьмёт свой формат по умолчанию.
export function pickRecordingMimeType(isTypeSupported: (type: string) => boolean): string | null {
  for (const type of MIME_CANDIDATES) {
    try {
      if (isTypeSupported(type)) return type
    } catch {
      // Safari бросает на незнакомом типе вместо false.
    }
  }
  return null
}

// «0:03» для таймера записи.
export function formatSeconds(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${s < 10 ? '0' : ''}${s}`
}

// Текст ошибки записи по имени DOMException из getUserMedia.
export function recordErrorText(name: string | undefined): string {
  switch (name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return 'Микрофон не разрешён. Разрешите его для сайта в настройках браузера и попробуйте ещё раз.'
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return 'Микрофон не найден.'
    case 'NotReadableError':
    case 'AbortError':
      return 'Микрофон занят другим приложением. Закройте его и попробуйте ещё раз.'
    default:
      return 'Запись не включилась. Попробуйте ещё раз.'
  }
}

// ── Трудные звуки: пары слов, которые русскоговорящий путает на слух ──
// Каждая строка группы: одно слово или пара, где отличается один звук.
// Озвучка через SpeakButtons (серверный TTS с кэшем), поэтому mp3 заранее не нужны.
export type SoundWord = { tr: string; ru: string }
export type SoundGroup = {
  id: string
  title: string
  // Подсказка, как ставить звук: коротко, по-русски, с опорой на знакомые слова.
  hint: string
  rows: SoundWord[][]
}

export const SOUND_GROUPS: SoundGroup[] = [
  {
    id: 'i',
    title: 'ı и i',
    hint: 'ı без точки похожа на русское "ы", только язык дальше назад и губы расслаблены. i с точкой звучит как русское "и". В паре меняется только этот звук.',
    rows: [
      [
        { tr: 'kır', ru: 'степь' },
        { tr: 'kir', ru: 'грязь' },
      ],
      [
        { tr: 'kıl', ru: 'волосок' },
        { tr: 'kil', ru: 'глина' },
      ],
      [
        { tr: 'dış', ru: 'наружный' },
        { tr: 'diş', ru: 'зуб' },
      ],
      [
        { tr: 'ılık', ru: 'тёплый' },
        { tr: 'ilik', ru: 'петля на одежде' },
      ],
    ],
  },
  {
    id: 'o',
    title: 'o и ö',
    hint: 'Для ö губы округлите как для "о", а язык поставьте вперёд, как для "э". Похоже на гласный в слове "тётя", но без мягкого "т" перед ним.',
    rows: [
      [
        { tr: 'gol', ru: 'гол' },
        { tr: 'göl', ru: 'озеро' },
      ],
      [
        { tr: 'on', ru: 'десять' },
        { tr: 'ön', ru: 'перёд' },
      ],
      [
        { tr: 'kor', ru: 'тлеющий уголь' },
        { tr: 'kör', ru: 'слепой' },
      ],
      [
        { tr: 'bol', ru: 'обильный' },
        { tr: 'böl', ru: 'раздели' },
      ],
    ],
  },
  {
    id: 'u',
    title: 'u и ü',
    hint: 'Для ü губы вытяните трубочкой как для "у", а язык поднимите вперёд, как для "и". Похоже на гласный в слове "тюль".',
    rows: [
      [
        { tr: 'uç', ru: 'кончик' },
        { tr: 'üç', ru: 'три' },
      ],
      [
        { tr: 'tur', ru: 'тур, поездка' },
        { tr: 'tür', ru: 'вид, тип' },
      ],
      [
        { tr: 'kul', ru: 'раб' },
        { tr: 'kül', ru: 'пепел' },
      ],
      [
        { tr: 'sur', ru: 'крепостная стена' },
        { tr: 'sür', ru: 'веди машину' },
      ],
    ],
  },
  {
    id: 'g',
    title: 'ğ, мягкое g',
    hint: 'Сама буква не звучит: она тянет гласную перед собой. dağ слышится как "даа", soğuk как "соук". Не произносите здесь "г".',
    rows: [
      [
        { tr: 'dağ', ru: 'гора' },
        { tr: 'yağmur', ru: 'дождь' },
      ],
      [
        { tr: 'soğuk', ru: 'холодный' },
        { tr: 'ağaç', ru: 'дерево' },
      ],
      [
        { tr: 'değil', ru: 'не' },
        { tr: 'öğretmen', ru: 'учитель' },
      ],
    ],
  },
  {
    id: 'stress',
    title: 'Ударение на последний слог',
    hint: 'В обычных словах ударение падает на последний слог: kitAP, kaLEM, öğrenCİ. Названия городов живут по своим правилам: İsTANbul, ANkara.',
    rows: [
      [
        { tr: 'kitap', ru: 'книга' },
        { tr: 'kalem', ru: 'ручка' },
      ],
      [
        { tr: 'öğrenci', ru: 'ученик' },
        { tr: 'teşekkürler', ru: 'спасибо' },
      ],
      [
        { tr: 'İstanbul', ru: 'Стамбул' },
        { tr: 'Ankara', ru: 'Анкара' },
      ],
    ],
  },
]

// Микрофон только у слов и коротких фраз (владелец 15.10.2026): абзац за
// диктором не повторить, и в лимит записи он не влезет.
export const RECORD_MAX_WORDS = 10
export function isRecordable(text: string | null | undefined): text is string {
  if (!text) return false
  const words = text.trim().split(/\s+/).filter(Boolean)
  return words.length > 0 && words.length <= RECORD_MAX_WORDS
}
